import { useState, useEffect, useRef } from 'react';

/**
 * MultiSelect — searchable multi-select over a fixed option list, styled to match
 * SearchableSelect. Selections show as removable chips; the dropdown stays open
 * while toggling so several picks take one trip.
 *
 * This is the id-based counterpart to TagInput (free-text strings) and the
 * multi-value counterpart to SearchableSelect (one value) — reach for it whenever
 * the user picks several rows out of a known list.
 *
 * Props:
 *   label       — optional label shown above the control
 *   options     — [{ value, label }]
 *   values      — currently selected values (compared as String, so int ids and
 *                 the string forms a <select> hands back both work)
 *   onChange    — called with the new array of values, in the options' own order
 *   placeholder — text shown when nothing is selected
 *   emptyText   — text shown when `options` is empty
 */
export default function MultiSelect({
  label,
  options,
  values = [],
  onChange,
  placeholder = '— none selected —',
  emptyText = 'No options available',
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selectedKeys = new Set(values.map(String));
  const selected = options.filter(o => selectedKeys.has(String(o.value)));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => String(o.label ?? '').toLowerCase().includes(query.toLowerCase()));

  // Emit in the options' order rather than click order, so the chips and the
  // stored list stay in the same (usually alphabetical) order as the dropdown.
  const toggle = (val) => {
    const next = new Set(selectedKeys);
    if (next.has(String(val))) next.delete(String(val));
    else next.add(String(val));
    onChange(options.filter(o => next.has(String(o.value))).map(o => o.value));
  };

  const remove = (val) => onChange(values.filter(v => String(v) !== String(val)));

  const labelStyle = { fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' };

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: '240px' }}>
      {label && <div style={labelStyle}>{label}</div>}
      <div onClick={() => setOpen(o => !o)} style={{
        border: `1px solid ${open ? '#2980b9' : '#ccc'}`, borderRadius: '4px',
        padding: '4px 8px', fontSize: '13px', background: '#fff', cursor: 'pointer',
        display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center',
        boxShadow: open ? '0 0 0 2px #d6eaf8' : 'none', minHeight: '32px',
      }}>
        {selected.length === 0
          ? <span style={{ color: '#aaa', flex: 1 }}>{placeholder}</span>
          : selected.map(o => (
            <span key={o.value} style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '2px 8px', borderRadius: '12px',
              background: '#eef4ff', color: '#2980b9', border: '1px solid #2980b933',
              fontSize: '12px',
            }}>
              {o.label}
              <span
                onClick={e => { e.stopPropagation(); remove(o.value); }}
                style={{ cursor: 'pointer', fontWeight: '700', lineHeight: 1 }}
              >×</span>
            </span>
          ))}
        <span style={{ color: '#aaa', marginLeft: 'auto', fontSize: '10px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: '#fff', border: '1px solid #2980b9', borderTop: 'none',
          borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          maxHeight: '240px', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>
            <input autoFocus value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Type to search…" onClick={e => e.stopPropagation()}
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #ddd',
                borderRadius: '3px', padding: '4px 8px', fontSize: '12px', outline: 'none' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {options.length === 0
              ? <div style={{ padding: '7px 12px', fontSize: '12px', color: '#bbb' }}>{emptyText}</div>
              : filtered.length === 0
                ? <div style={{ padding: '7px 12px', fontSize: '12px', color: '#bbb' }}>No results</div>
                : filtered.map(o => {
                  const on = selectedKeys.has(String(o.value));
                  return (
                    <div key={o.value} onMouseDown={e => { e.preventDefault(); toggle(o.value); }} style={{
                      padding: '7px 12px', fontSize: '13px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '8px',
                      background: on ? '#eef4ff' : 'transparent',
                      color: on ? '#2980b9' : '#2c3e50',
                      fontWeight: on ? '600' : 'normal',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f5f8ff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = on ? '#eef4ff' : 'transparent'; }}
                    >
                      <input type="checkbox" checked={on} readOnly tabIndex={-1} style={{ pointerEvents: 'none' }} />
                      {o.label}
                    </div>
                  );
                })}
          </div>
        </div>
      )}
    </div>
  );
}
